import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Paper, Typography } from '@mui/material';

const DiscountOptions = ({
    basePrice,
    discountAmount,
    onDiscountChange,
    completionFee,
    onCompletionFeeChange,
    customPrice,
    onCustomPriceChange,
    hasHugeSalePrivilege,
    isSuperAdmin = false,
    maxDiscountPercentage = 20,
    t
}) => {
    // Calculate max discount based on user role
    const maxDiscountAmount = isSuperAdmin
        ? basePrice
        : basePrice * (maxDiscountPercentage / 100);

    // Handle completion fee change - allow free typing
    const handleCompletionFeeChange = (e) => {
        const value = e.target.value;
        onCompletionFeeChange(value);
    };

    // Handle custom price change - allow free typing
    const handleCustomPriceChange = (e) => {
        const value = e.target.value;
        onCustomPriceChange(value);
    };

    // Handle discount change from text input — NO clamping, just pass through
    const handleDiscountTextChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            onDiscountChange('');
            return;
        }
        // Allow free typing — validation happens in parent (AppointmentForm)
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0) {
            onDiscountChange(value); // Pass raw string, not clamped
        } else if (value === '0' || value === '0.' || /^\d*\.?\d*$/.test(value)) {
            // Allow intermediate typing like "0.", "1.", etc.
            onDiscountChange(value);
        }
    };

    // Handle discount change from slider — slider IS clamped to max
    const handleDiscountSliderChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        onDiscountChange(value);
    };

    // Check if current discount exceeds limit (for visual feedback)
    const currentDiscount = parseFloat(discountAmount) || 0;
    const isOverLimit = !isSuperAdmin && basePrice > 0 && currentDiscount > maxDiscountAmount;

    return (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '33%' }}>
                            {t('appointments.discount') || 'Discount'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '33%' }}>
                            {t('appointments.completion') || 'Completion'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '33%' }}>
                            {t('appointments.makeHugeSale') || 'Make Huge Sale'}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box>
                                    <TextField
                                        type="text"
                                        inputMode="decimal"
                                        value={discountAmount === null || discountAmount === undefined ? '' : discountAmount}
                                        onChange={handleDiscountTextChange}
                                        size="small"
                                        placeholder="0.00"
                                        sx={{ width: 100 }}
                                        error={isOverLimit}
                                        helperText={
                                            isSuperAdmin
                                                ? null
                                                : `Max: ${maxDiscountAmount.toFixed(2)}`
                                        }
                                    />
                                </Box>
                                <input
                                    title="Discount Slider"
                                    type="range"
                                    min={0}
                                    max={maxDiscountAmount}
                                    step={0.01}
                                    value={Math.min(currentDiscount, maxDiscountAmount)}
                                    onChange={handleDiscountSliderChange}
                                    style={{ flex: 1, maxWidth: 120 }}
                                />
                            </Box>
                        </TableCell>
                        <TableCell>
                            <TextField
                                type="text"
                                inputMode="decimal"
                                name="completionFee"
                                value={completionFee === null || completionFee === undefined ? '' : completionFee}
                                onChange={handleCompletionFeeChange}
                                size="small"
                                placeholder="0.00"
                                sx={{ width: 100 }}
                            />ذ
                        </TableCell>
                        <TableCell>
                            {hasHugeSalePrivilege && (
                                <TextField
                                    type="text"
                                    inputMode="decimal"
                                    name="customPrice"
                                    value={customPrice === null || customPrice === undefined ? '' : customPrice}
                                    onChange={handleCustomPriceChange}
                                    size="small"
                                    placeholder="0.00"
                                    sx={{ width: 100 }}
                                />
                            )}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DiscountOptions;